import { useMemo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  AppSegment,
  AppFlexContainer,
  AppFlexItem,
  AppButton,
  AppDivider,
} from 'client/ui-building-blocks'

import { AccountStatus } from 'cat-shared/utils'
import { getAccountStatus, isAccountConfirmed } from 'client/services'
import { useAppContext, usePageStateContext } from 'client/hooks'

import AppSettingsForm from './AppSettingsForm/app-settings-form'
import UpdateUserDetailsForm from './UpdateUserDetailsForm/update-user-details-form'
import DeactivateAccounForm from './DeactivateAccountForm/deactivate-account-form'
import UpdatePasswordForm from './UpdatePasswordForm/update-password-form'
import UploadAvatarForm from './UploadAvatarForm/upload-avatar-form'
import Payment from './Payment/payment'

const SettingsPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { authUser } = useAppContext()

  const {
    settingsPageState: { tabNo },
    setSettingsPageState,
  } = usePageStateContext()

  const accountStatus = getAccountStatus(authUser)

  const onTabSwitch = useCallback(
    (tabNo: number) => {
      return () => {
        if (!queryClient.isFetching() && !queryClient.isMutating()) {
          setSettingsPageState({ tabNo })
          return true
        }
        return false
      }
    },
    [queryClient, setSettingsPageState]
  )

  const accountConfirmed = isAccountConfirmed(accountStatus)

  const menuItems = useMemo(() => {
    if (!authUser) return []
    return [
      {
        label: t('settingsPage.tabs.appSettings'),
        onClick: onTabSwitch(1),
      },
      {
        label: t('settingsPage.tabs.personalDetails'),
        onClick: onTabSwitch(2),
      },
      {
        label: t('settingsPage.tabs.password'),
        onClick: onTabSwitch(3),
      },
      {
        label: t('settingsPage.tabs.profilePicture'),
        onClick: onTabSwitch(4),
        disabled: !accountConfirmed,
      },
      {
        label: t('settingsPage.tabs.payment'),
        onClick: onTabSwitch(5),
        disabled:
          !accountConfirmed || authUser.config.payment?.type === AccountStatus.FREE,
        hide: true,
      },
      {
        label: t('settingsPage.tabs.deactivation'),
        onClick: onTabSwitch(6),
      },
    ] as {
      label: string
      onClick: () => boolean
      disabled?: boolean
      hide?: boolean
    }[]
  }, [accountConfirmed, authUser, onTabSwitch, t])

  if (!authUser) return null

  return (
    <AppSegment
      headerProps={{
        text: t('settingsPage.header'),
        iconProps: {
          name: 'SETTING',
        },
      }}
      maxWidth="medium"
      centered
    >
      <AppFlexContainer rowGap="25px">
        <AppFlexItem flexGrow={1}>
          <AppFlexContainer rowGap="5px" columnGap="5px">
            {menuItems.map((item, i) => {
              if (item.hide) {
                return null
              }
              return (
                <AppFlexItem key={i} flexGrow={1} maxWidth="150px">
                  <AppButton
                    variant={i + 1 === tabNo ? 'success' : 'outlined'}
                    text={item.label}
                    fullWidth
                    disabled={item.disabled}
                    onClick={item.onClick}
                  />
                </AppFlexItem>
              )
            })}
          </AppFlexContainer>
          <AppDivider border size="large" />
        </AppFlexItem>
        <AppFlexItem minWidth="100%">
          {tabNo === 1 ? <AppSettingsForm /> : null}
          {tabNo === 2 ? <UpdateUserDetailsForm /> : null}
          {tabNo === 3 ? <UpdatePasswordForm /> : null}
          {tabNo === 4 ? <UploadAvatarForm userId={authUser._id.toString()} /> : null}
          {tabNo === 5 ? <Payment /> : null}
          {tabNo === 6 ? <DeactivateAccounForm /> : null}
        </AppFlexItem>
      </AppFlexContainer>
    </AppSegment>
  )
}

export default SettingsPage
